var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { v4 as uuidv4 } from "uuid";
var RESTMETHOD;
(function (RESTMETHOD) {
    RESTMETHOD["POST"] = "POST";
    RESTMETHOD["GET"] = "GET";
    RESTMETHOD["PATCH"] = "PATCH";
    RESTMETHOD["PUT"] = "PUT";
    RESTMETHOD["DELETE"] = "DELETE";
})(RESTMETHOD || (RESTMETHOD = {}));
class ExtendedConsole {
    constructor() {
        this.originalConsole = console;
    }
    log(...args) {
        this.originalConsole.log(...args);
    }
    info(...args) {
        this.originalConsole.info("[INFO]:", ...args);
    }
    warn(...args) {
        this.originalConsole.warn(...args);
    }
    error(...args) {
        this.originalConsole.error("[ERROR]:", ...args);
    }
    http(...args) {
        console.log("[HTTP]:", ...args);
    }
}
export class BaseApi {
    constructor(headers, showlog = false) {
        var _a;
        this.headers = headers;
        this.showlog = showlog;
        this.showHeaders = false;
        this.showPayload = false;
        this.showResponse = false;
        this.fetch = fetch; // http fetch
        this.isExternalFetch = false;
        this.logger = new ExtendedConsole();
        this.skipHeaders = new Set(["transfer-encoding", "content-encoding"]);
        this.compressionTypes = new Set(["gzip", "br", "deflate"]);
        if ((_a = this.headers) === null || _a === void 0 ? void 0 : _a["content-length"]) {
            delete this.headers["content-length"];
        }
    }
    /**
     * @param {typeof console} l i.e. - const mylogger = new console.Console(out, err);
     */
    setLogger(l) {
        this.logger = l;
    }
    setHost(host) {
        this.host = host;
    }
    setFetch(val) {
        this.fetch = val;
        this.isExternalFetch = true;
    }
    withHeaders() {
        this.showHeaders = true;
        return this;
    }
    withPayload() {
        this.showPayload = true;
        return this;
    }
    withResponse() {
        this.showResponse = true;
        return this;
    }
    setCookies(cookies, names) {
        if (!this.cookies) {
            this.cookies = {};
        }
        for (const n of names) {
            const value = cookies.get(n);
            if (value) {
                this.cookies[n] = value;
            }
        }
    }
    multi(...apiCalls) {
        return __awaiter(this, void 0, void 0, function* () {
            const resp = yield Promise.allSettled(apiCalls.map(({ method = RESTMETHOD.GET, url, payload, afterReceivedData }) => {
                let task;
                switch (method) {
                    case RESTMETHOD.POST:
                        task = this.post(url, payload);
                        break;
                    case RESTMETHOD.GET:
                        task = this.get(url, payload);
                        break;
                    case RESTMETHOD.PATCH:
                        task = this.patch(url, "", payload);
                        break;
                    case RESTMETHOD.DELETE:
                        task = this.delete(url, "");
                        break;
                }
                return afterReceivedData ? afterReceivedData(task) : task;
            }));
            return resp.map(v => {
                if (v.status !== "fulfilled") {
                    throw v.reason;
                }
                return v.value;
            });
        });
    }
    // Read
    get(path, payload, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.raw(this.genQuery(path, payload), Object.assign(Object.assign({}, options), { method: "GET" }), true);
        });
    }
    // Create
    post(path, payload, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.raw(this.sanitizePath(path), Object.assign(Object.assign(Object.assign({}, options), { method: "POST" }), (payload ? { body: JSON.stringify(payload) } : {})), true);
        });
    }
    // Update
    patch(path, id, payload, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.raw(this.sanitizePath(`${path}/${id}`), Object.assign(Object.assign(Object.assign({}, options), { method: "PATCH" }), (payload ? { body: JSON.stringify(payload) } : {})), true);
        });
    }
    // Delete
    delete(path, id, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.raw(this.sanitizePath([path, id].filter(v => v).join("/")), Object.assign(Object.assign({}, options), { method: "DELETE" }), true);
        });
    }
    // Read
    getRaw(path, payload, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.raw(this.genQuery(path, payload), Object.assign(Object.assign({}, options), { method: "GET" }), false);
        });
    }
    // Create
    postRaw(path, payload, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.raw(this.sanitizePath(path), Object.assign(Object.assign(Object.assign({}, options), { method: "POST" }), (payload ? { body: JSON.stringify(payload) } : {})), false);
        });
    }
    // Post FormData as multipart/form-data
    postFormData(path, payload, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const fetchOptions = Object.assign(Object.assign(Object.assign({}, options), { method: "POST", body: payload, headers: Object.assign(Object.assign({}, this.headers), (this.cookies
                    ? {
                        cookie: Object.entries(this.cookies)
                            .map(([k, v]) => `${k}=${v}`)
                            .join(";"),
                    }
                    : {})) }), options);
            this.addHeaderMandatoryFields(fetchOptions.headers);
            const url = this.sanitizePath(path);
            if (this.showlog) {
                this.logger.http(url.href, "requesting");
                this.showHeaders && this.logger.info(`header: ${JSON.stringify(fetchOptions.headers)}`);
                this.showPayload && this.logger.info("payload:", payload);
            }
            const response = yield this.fetch(url, fetchOptions);
            this.afterResponse(response);
            this.showlog && this.logger.info(url.href, `response status:`, response.status, response.statusText);
            this.showlog && this.showResponse && this.logger.info(url.href, "response raw: ", response.body);
            if (this.compressionTypes.has(`${response.headers.get("content-encoding")}`.trim().toLowerCase())) {
                return yield this.uncompressResponse(response);
            }
            return response;
        });
    }
    // Update
    patchRaw(path, id, payload, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.raw(this.sanitizePath([path, id].filter(v => v).join("/")), Object.assign(Object.assign(Object.assign({}, options), { method: "PATCH" }), (payload ? { body: JSON.stringify(payload) } : {})), false);
        });
    }
    // Put - replace
    putRaw(path, id, payload, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.raw(this.sanitizePath([path, id].filter(v => v).join("/")), Object.assign(Object.assign(Object.assign({}, options), { method: "PUT" }), (payload ? { body: JSON.stringify(payload) } : {})), false);
        });
    }
    // Delete
    deleteRaw(path, id, options) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.raw(this.sanitizePath([path, id].filter(v => v).join("/")), Object.assign(Object.assign({}, options), { method: "DELETE" }), false);
        });
    }
    raw(url_1, options_1) {
        return __awaiter(this, arguments, void 0, function* (url, options, toJson = true) {
            const { body } = options || {};
            const hasBody = (body === null || body === void 0 ? void 0 : body.length) > 0;
            let bodySize = 0;
            if (hasBody) {
                const blob = new Blob([body], { type: "application/json" });
                bodySize = blob.size;
            }
            const rqId = uuidv4();
            const fetchOptions = Object.assign({ headers: Object.assign(Object.assign(Object.assign({ "content-type": "application/json", "x-rqid": rqId }, this.headers), (hasBody ? { "content-length": `${bodySize}` } : {})), (this.cookies
                    ? {
                        cookie: Object.entries(this.cookies)
                            .map(([k, v]) => `${k}=${v}`)
                            .join(";"),
                    }
                    : {})) }, options);
            this.addHeaderMandatoryFields(fetchOptions.headers);
            if (this.showlog) {
                this.logger.http(url.href, "requesting");
                this.showHeaders && this.logger.info(`header: ${JSON.stringify(fetchOptions.headers)}`);
                this.showPayload && this.logger.info(`payload: ${body}`);
            }
            this.logger.warn(`${rqId} > fetch() >`, url.href);
            const response = yield this.fetch(url, fetchOptions);
            this.afterResponse(response);
            this.showlog && this.logger.info(url.href, `response status:`, response.status, response.statusText);
            if (!toJson) {
                this.showlog && this.showResponse && this.logger.info(url.href, "response raw: ", response.body);
                // Support for compression
                if (this.compressionTypes.has(`${response.headers.get("content-encoding")}`.trim().toLowerCase())) {
                    return yield this.uncompressResponse(response);
                }
                // Rewrap response so svelte doesn't throw an error when setting cookies
                if (response.headers.has("set-cookie")) {
                    const headersObj = {};
                    for (const [key, value] of response.headers.entries()) {
                        if (this.skipHeaders.has(key.trim().toLowerCase())) {
                            continue;
                        }
                        headersObj[key] = value;
                    }
                    return new Response(yield response.arrayBuffer(), {
                        headers: headersObj,
                        status: response.status,
                        statusText: response.statusText,
                    });
                }
                return response;
            }
            // @TODO:- remove once everything is converted to raw()
            // Handle error responses
            if (response.status >= 400) {
                const msg = `${response.status} - ${response.statusText}`;
                this.showlog && this.logger.error(url.href, msg);
                throw new Error(msg);
            }
            const data = (yield response.json());
            this.showlog && this.showResponse && this.logger.info(url.href, "response json: ", data);
            return data;
        });
    }
    uncompressResponse(response) {
        return __awaiter(this, void 0, void 0, function* () {
            const headersObj = {};
            for (const [key, value] of response.headers.entries()) {
                if (this.skipHeaders.has(key.trim().toLowerCase())) {
                    continue;
                }
                headersObj[key] = value;
            }
            try {
                let body;
                const contentType = response.headers.get("Content-Type");
                if (contentType === null || contentType === void 0 ? void 0 : contentType.includes("application/json")) {
                    body = JSON.stringify(yield response.json());
                }
                else if (contentType === null || contentType === void 0 ? void 0 : contentType.startsWith("text/")) {
                    body = yield response.text();
                }
                else {
                    body = yield response.arrayBuffer(); // For binary data
                }
                return new Response(body, { headers: headersObj, status: response.status, statusText: response.statusText });
            }
            catch (err) {
                console.error(err);
                return new Response(undefined, { headers: headersObj, status: 500, statusText: "Error uncompressing response" });
            }
        });
    }
    sanitizePath(path) {
        const url = path.replace(this.host, "").replace(/\/\//g, "/");
        return new URL(this.host + (!url.startsWith("/") ? "/" : "") + url);
    }
    genQuery(path, payload) {
        const url = this.sanitizePath(path);
        if (payload) {
            Object.entries(payload).forEach(([key, value]) => {
                if (key === "select" && Array.isArray(value)) {
                    value = value.join(",");
                }
                else if (key === "populate" && Array.isArray(value)) {
                    value = value.join("|");
                }
                else if (key === "filter" && Array.isArray(value)) {
                    value = "~" + value.map(v => (v.startsWith("~") ? v.substring(1).trim() : v)).join("~");
                }
                url.searchParams.set(key, value);
            });
        }
        return url;
    }
    afterResponse(response) { }
    rawFetch(path, reqInit) {
        const url = this.sanitizePath(path);
        if (reqInit.headers) {
            this.addHeaderMandatoryFields(reqInit.headers);
        }
        return this.fetch(url, reqInit);
    }
    addHeaderMandatoryFields(headers) { }
}
